import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { NavController } from '@ionic/angular';
import { from, Observable } from 'rxjs';
import { mapTo, mergeMap } from 'rxjs/operators';
import { endpoints } from 'src/app/core/config/endpoints';
import { ApiService } from '../../api/api.service';
import { LocalStorageService } from '../../storage/local-storage/local-storage.service';
import { LoggerService } from '../../logger/logger.service';

export interface LoginResponse {
	token: string;
	userId: number;
}

@Injectable({
	providedIn: 'root'
})
export class AuthService {

	private readonly JWT_TOKEN = 'JWT_TOKEN';
	private readonly USER_ID = 'USER_ID';

	constructor(
		public storage: LocalStorageService,
		public api: ApiService,
		private navController: NavController,
		private router: Router,
		private logger: LoggerService
	) { }

	login(username: string, password: string): Observable<boolean> {
		return this.api.post<LoginResponse>(endpoints.login, { username, password })
			.pipe(
				mergeMap((response: LoginResponse) => from(this.handleResponse(response))),
				mapTo(true)
			);
	}

	async logout(): Promise<void> {

		this.logger.appInsights.clearAuthenticatedUserContext();
		await this.storage.clearDynamicTables();
		this.navController.setDirection('root');
		await this.router.navigate(['login']);
	}

	async handleResponse(response: LoginResponse): Promise<void> {
		await this.setAuthToken(response.token);
		await this.setUserId(response.userId);
	}

	async setAuthToken(token: string): Promise<void> {
		await this.storage.auth.setItem(this.JWT_TOKEN, token);
	}

	async setUserId(userId: number): Promise<void> {
		this.logger.setAuthenticatedUserContext(userId.toString());
		await this.storage.auth.setItem(this.USER_ID, userId);
	}

	async isLoggedIn(): Promise<boolean> {
		const isLoggedIn = !!(await this.getAuthToken());
		if (isLoggedIn) {
			this.logger.setAuthenticatedUserContext((await this.getCurrentUserId()).toString());
		} else {
			this.logger.clearAuthenticatedUserContext();
		}
		return isLoggedIn;
	}

	async getAuthToken(): Promise<string | null> {
		return await this.storage.auth.getItem(this.JWT_TOKEN);
	}

	async getCurrentUserId(): Promise<number> {
		return await this.storage.auth.getItem(this.USER_ID);
	}
}
